import React, { useEffect, useMemo, useRef } from "react";

export type SmsCodeCirclesProps = {
  length: number;
  value: string;
  disabled?: boolean;
  autoFocus?: boolean;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  autoComplete?: React.InputHTMLAttributes<HTMLInputElement>["autoComplete"];
  hasError?: boolean;
  onChange: (nextValue: string) => void;
  onComplete?: (finalValue: string) => void;
  "aria-label"?: string;
  className?: string;
};

function normalizeDigits(value: string, length: number) {
  return value.replace(/\D/g, "").slice(0, length);
}

export function SmsCodeCircles({
  length,
  value,
  disabled = false,
  autoFocus = false,
  inputMode = "numeric",
  autoComplete = "one-time-code",
  hasError = false,
  onChange,
  onComplete,
  "aria-label": ariaLabel = "Verification code",
  className,
}: SmsCodeCirclesProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const normalizedValue = useMemo(() => {
    return normalizeDigits(value ?? "", length);
  }, [value, length]);

  useEffect(() => {
    if (autoFocus && !disabled) {
      inputRef.current?.focus();
    }
  }, [autoFocus, disabled]);

  useEffect(() => {
    if (normalizedValue.length === length) {
      onComplete?.(normalizedValue);
    }
  }, [normalizedValue, length, onComplete]);

  const circleBase =
    "w-8 h-8 border-2 border-orange-500 text-orange-500 rounded-full flex items-center justify-center transition-colors text-sm font-medium text-foreground";
  const circleFilled = "bg-orange-300";
  const circleEmpty = "bg-transparent border-muted-foreground/40";
  const circleError = "border-destructive";
  const circleDisabled = "opacity-50";
  const wrapperBase = "inline-flex justify-center items-center gap-2";
  const wrapperInteractive = disabled ? "cursor-not-allowed" : "cursor-text";

  return (
    <div
      className={[wrapperBase, wrapperInteractive, className]
        .filter(Boolean)
        .join(" ")}
    >
      <input
        ref={inputRef}
        type="text"
        value={normalizedValue}
        disabled={disabled}
        inputMode={inputMode}
        autoComplete={autoComplete}
        aria-label={ariaLabel}
        aria-invalid={hasError || undefined}
        onChange={(e) => {
          const next = normalizeDigits(e.target.value, length);
          onChange(next);
        }}
        onKeyDown={(e) => {
          if (disabled) return;

          if (e.key === "Backspace") {
            e.preventDefault();
            onChange(normalizedValue.slice(0, -1));
            return;
          }

          if (e.key.length === 1) {
            const isDigit = /\d/.test(e.key);
            if (!isDigit) {
              e.preventDefault();
              return;
            }
            e.preventDefault();
            if (normalizedValue.length >= length) return;
            const next = normalizeDigits(normalizedValue + e.key, length);
            onChange(next);
          }
        }}
        onPaste={(e) => {
          if (disabled) return;
          e.preventDefault();
          const pasted = e.clipboardData.getData("text");
          const next = normalizeDigits(normalizedValue + pasted, length);
          onChange(next);
        }}
        className="absolute w-px h-px opacity-0 pointer-events-none"
      />

      <button
        type="button"
        disabled={disabled}
        aria-label="Enter verification code"
        onClick={() => inputRef.current?.focus()}
        className="inline-flex items-center gap-2"
      >
        {Array.from({ length }).map((_, index) => {
          const digit = normalizedValue[index] ?? "";
          const filled = digit !== "";
          const classes = [
            circleBase,
            filled ? circleFilled : circleEmpty,
            hasError ? circleError : null,
            disabled ? circleDisabled : null,
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <span key={index} className={classes} aria-hidden="true">
              {digit}
            </span>
          );
        })}
      </button>
    </div>
  );
}

export default SmsCodeCircles;
